<template>
  <v-container v-if="teams[$route.params.team].account">
    <v-row>
      <v-col stretch>
        <v-breadcrumb
          v-if="$route.name === 'createContact'"
          :items="breadcrumbCreateContactItems"
          :mobile="breadcrumbMobileCreateItem"
        ></v-breadcrumb>
        <v-breadcrumb
          v-if="$route.name === 'editContact'"
          :items="breadcrumbEditContactItems"
          :mobile="breadcrumbMobileEditItem"
        ></v-breadcrumb>
      </v-col>
    </v-row>
    <v-row>
      <v-col stretch>
        <v-title-bar v-if="$route.name === 'createContact'" wrap :title="$t('titles.createContact')">
          <v-button class="wrap-btn" primary @click.native="submitContact()">{{
            $t("actions.createContact")
          }}</v-button>
        </v-title-bar>
        <v-title-bar v-if="$route.name === 'editContact'" wrap :title="$t('titles.editContact')">
          <v-button class="wrap-btn" primary @click.native="submitContact(true)">{{ $t("actions.save") }}</v-button>
        </v-title-bar>
      </v-col>
    </v-row>
    <form @submit.prevent>
      <v-row class="mb-0">
        <v-col class="col-12 md:col-6">
          <label for="gender">{{ $t("labels.contactData") }}</label>
          <v2-select
            :placeholder="$t('contact.gender')"
            :showLabels="false"
            v-model="gender"
            id="gender"
            optionLabel="label"
            :options="genderOptions"
          ></v2-select>
          <v-textfield :placeholder="$t('contact.firstName')" v-model="firstName" />
          <v-textfield
            :placeholder="$t('contact.lastName')"
            v-model="lastName"
            :required="true"
            v-validate="'required'"
            name="lastname"
            :error="errors.first('lastname')"
            :data-vv-as="$t('contact.lastName')"
          />
          <v-textfield
            :placeholder="$t('contact.email')"
            v-model.trim="email"
            type="email"
            :required="true"
            v-validate="'required|email'"
            name="email"
            :error="errors.first('email')"
            :data-vv-as="$t('contact.email')"
          />
          <v-textfield :placeholder="$t('contact.tel')" v-model="tel" />
          <v-textfield :placeholder="$t('contact.mobile')" v-model="mobile" />
          <v-textfield :placeholder="$t('contact.fax')" v-model="fax" />
        </v-col>
        <v-col class="col-12 md:col-6 flex flex-col" style="display: flex !important">
          <div class="flex justify-between">
            <label>{{ $t("labels.contactImage") }}</label>
            <v-button
              v-if="$route.name === 'editContact' && currentProfileImage"
              danger
              round
              icon
              class="flex justify-center items-center"
              style="width: 30px; height: 30px"
              @click.native="removeImage()"
            >
              <v2-icon :data="require('@icon/Icon=trash.svg')" width="18" height="18"></v2-icon>
            </v-button>
          </div>
          <v-row class="mb-0 flex-1">
            <v-col
              class="col-12 p-0"
              v-if="$route.name === 'editContact' && !lastProfileImage && currentContact && currentContact.image"
            >
              <v-list-item simple flexibleHeight>
                <v-list-cell size="1">
                  <div slot="value" class="w-full flex justify-center">
                    <v-spinner :loading="!isReady" style="width: auto; height: auto"></v-spinner>
                    <v-lazy-load v-if="isReady" :image="imageUrl" placeholderFileName="defaultContact"></v-lazy-load>
                  </div>
                </v-list-cell>
              </v-list-item>
              <div>{{ $t("stati.files.text." + currentStatus) }}</div>
            </v-col>
            <v-col
              class="col-12 p-0 mt-0"
              style="height: 444px"
              v-show="
                $route.name === 'createContact' ||
                ($route.name === 'editContact' && (lastProfileImage || (currentContact && !currentContact.image)))
              "
            >
              <div style="width: 100%" :class="['backoffice-uppy mt-0 h-full']" :id="'trigger-envivo-upload-box'"></div>
              <v2-uppy-upload
                :locale="teams[$route.params.team].account.locale"
                target="#trigger-envivo-upload-box"
                componentTagname="tb-image"
                :inline="true"
                :hideUploadButton="true"
                @addedFileEvent="
                  (file) => {
                    handleFile(file);
                  }
                "
                :autoProceed="false"
              ></v2-uppy-upload>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row class="mb-0">
        <v-col class="col-12 md:col-6">
          <label for="contactCompanyInfo">{{ $t("labels.contactCompanyInfo") }}</label>
          <v-textfield :placeholder="$t('contact.company')" v-model="company" id="contactCompanyInfo" />
          <v-textfield :placeholder="$t('contact.jobTitle')" v-model="jobTitle" />
          <v-textfield :placeholder="$t('contact.jobUnit')" v-model="jobUnit" />
          <v-textfield
            :placeholder="$t('contact.website')"
            v-model="website"
            name="website"
            v-validate="'url:require_protocol'"
            :error="errors.first('website')"
            :data-vv-as="$t('contact.website')"
          />
        </v-col>
        <v-col class="col-12 md:col-6">
          <label for="contactAddress">{{ $t("labels.contactAddress") }}</label>
          <v-textfield :placeholder="$t('contact.street')" v-model="street" id="contactAddress" />
          <v-textfield :placeholder="$t('contact.zip')" v-model="zip" />
          <v-textfield :placeholder="$t('contact.city')" v-model="city" />
          <v2-select
            :placeholder="$t('contact.country')"
            :showLabels="false"
            v-model="country"
            optionLabel="label"
            :options="countryOptions"
          ></v2-select>
        </v-col>
      </v-row>
      <v-row class="mb-0">
        <v-col class="col-12 md:col-6">
          <label for="contactTimeAndLang">{{ $t("labels.contactTimeAndLang") }}</label>
          <v2-select
            withRequiredLabel
            :placeholder="$t('contact.locale')"
            v-model="locale"
            :options="localeOptions"
            optionLabel="label"
            id="contactTimeAndLang"
            name="locale"
            v-validate="'required'"
            :error="errors.first('locale')"
            :data-vv-as="$t('contact.locale')"
          ></v2-select>
          <v2-select
            withRequiredLabel
            :placeholder="$t('placeholders.selectTimezone')"
            v-model="timezone"
            id="timezone"
            name="timezone"
            optionLabel="label"
            trackBy="label"
            v-validate="'required'"
            :data-vv-as="$t('contact.timezone')"
            :error="errors.first('timezone')"
            :options="$t('timezones')"
          ></v2-select>
        </v-col>
        <v-col class="col-12 md:col-6">
          <label for="contactDefaultForUser">{{ $t("labels.contactDefaultForUser") }}</label>
          <v2-select
            :placeholder="$t('placeholders.selectUsers')"
            v-model="users"
            :options="userOptions"
            :multiple="true"
            optionLabel="label"
            id="contactDefaultForUser"
            name="client"
            :searchable="true"
            :loading="isLoading"
            :internalSearch="true"
            :customLabel="setCustomLabel"
            trackBy="uid"
            :hideSelected="true"
            :clearOnSelect="false"
            openDir="bottom"
          ></v2-select>
        </v-col>
        <v-col stretch>
          <div class="w-full flex justify-end">
            <v-button v-if="$route.name === 'createContact'" primary @click.native="submitContact()">{{
              $t("actions.createContact")
            }}</v-button>
            <v-button v-if="$route.name === 'editContact'" primary @click.native="submitContact(true)">{{
              $t("actions.save")
            }}</v-button>
          </div>
        </v-col>
      </v-row>
      <v-row class="mb-0">
        <v-col class="col-12 md:col-6">
          <label>{{ $t("labels.contactSocialMediaAccounts") }}</label>
          <v-textfield
            :placeholder="$h.capitalizeFirstLetter(key)"
            v-model="socialMediaAccounts[key]"
            v-for="(value, key) in socialMediaAccounts"
            :key="key"
            v-validate="'regex:^https?:\\/\\/(.*)'"
            :name="key"
            :error="errors.first(key) ? $t('veeValidate.messages.https') : ''"
            :data-vv-as="key"
          />
        </v-col>
      </v-row>
    </form>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "FormClient",
  data() {
    return {
      uploadConfig: {
        max_size: 5000,
      },
      locale: { label: this.$t("lang.de"), value: "de" },
      timezone: { label: "Berlin (GMT+2)", value: "Europe/Berlin" },
      lastName: "",
      firstName: "",
      email: "",
      street: "",
      zip: "",
      city: "",
      country: "",
      company: "",
      tel: "",
      mobile: "",
      fax: "",
      website: "",
      jobTitle: "",
      jobUnit: "",
      gender: "",
      users: [],
      socialMediaAccounts: {
        facebook: "",
        twitter: "",
        linkedin: "",
        xing: "",
      },
      profileImg: null,
      file: null,
      isLoading: false,
      amountTry: 10,
      timeWaitStart: 3000,
      timer: null,
      currentProfileImage: {},
      imageUrl: "",
      lastProfileImage: null,
      allowedFileTypes: ["image/jpeg", "image/png"],
      customFileErrors: [],
      localeOptions: [],
      genderOptions: [
        { label: this.$t("gender.female"), value: "f" },
        { label: this.$t("gender.male"), value: "m" },
      ],
      countryOptions: [],
      userOptions: [],
      breadcrumbCreateContactItems: [
        {
          label: this.$t("menus.settings"),
          route: { name: "Settings", params: { tab: "account" } },
        },
        {
          label: this.$t("menus.contacts"),
          route: { name: "Settings", params: { tab: "contacts" } },
        },
        {
          label: this.$t("menus.createContact"),
          active: true,
        },
      ],
      breadcrumbEditContactItems: [
        {
          label: this.$t("menus.settings"),
          route: { name: "Settings", params: { tab: "account" } },
        },
        {
          label: this.$t("menus.contacts"),
          route: { name: "Settings", params: { tab: "contacts" } },
        },
        {
          label: "",
          route: {},
        },
        {
          label: this.$t("menus.editContact"),
          active: true,
        },
      ],
      breadcrumbMobileCreateItem: {
        label: this.$t("menus.contacts"),
        route: { name: "Settings", params: { tab: "contacts" } },
      },
      breadcrumbMobileEditItem: {
        label: "",
        route: { name: "Contact", params: { uid: "" } },
      },
    };
  },
  created() {
    const vm = this;
    if (vm.$route.name === "editContact") {
      vm.$store.commit("GLOBAL_CUSTOM_LOADING_STARTED");
      vm.getContact({ contact: vm.$route.params.uid }).then((res) => {
        vm.currentProfileImage = res.image;
        vm.untilReady();
        this.breadcrumbEditContactItems[2].label = this.$h.formatName(this.currentContact);
        this.breadcrumbEditContactItems[2].route = {
          name: "Contact",
          params: { uid: this.currentContact.uid },
        };
        this.breadcrumbMobileEditItem.label = this.$h.formatName(this.currentContact);
        this.breadcrumbMobileEditItem.route = {
          name: "Contact",
          params: { uid: this.currentContact.uid },
        };
        vm.getLanguages()
          .then(() => {
            vm.setOptions()
              .then(() => {
                vm.setValues();
                vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
              })
              .catch(() => {
                vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
              });
          })
          .catch(() => {
            vm.$store.commit("GLOBAL_CUSTOM_LOADING_ENDED");
          });
      });
    } else {
      vm.$store.commit("TEAMS_RESET_CURRENT_CONTACT");
      vm.setOptions();
      vm.getLanguages();
    }
    vm.transformCountryOptions();
  },
  destroyed() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  },
  methods: {
    ...mapActions([
      "getTeamLanguages",
      "getUsers",
      "getContact",
      "createContact",
      "createContactImage",
      "editContact",
      "getContactImagePreview",
      "deleteContactImage",
    ]),
    formattedName(user) {
      let name = this.$h.formatName(user, true);
      return `${name}`;
    },
    getLanguages() {
      return new Promise((resolve, reject) => {
        this.getTeamLanguages()
          .then(() => {
            if (this.teamLanguages) {
              this.teamLanguages.forEach((lang) => {
                this.localeOptions.push({
                  label: this.$t('lang["' + lang + '"]'),
                  value: lang,
                });
              });
            }
            resolve();
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    setOptions() {
      return new Promise((resolve, reject) => {
        const params = {
          per_page: "1000",
          order_col: "last_name",
          order_dir: "ASC",
          search: "",
        };
        this.getUsers({ params, customLoading: true })
          .then((res) => {
            this.userOptions = res.data.data;
            resolve();
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    setCustomLabel(user) {
      return this.$h.formatName(user);
    },
    transformCountryOptions() {
      const countries = this.$t("countries");
      Object.keys(countries).forEach((key) => {
        this.countryOptions.push({ label: countries[key], value: key });
      });
    },
    handleFile(file) {
      this.profileImg = file;
      this.file = file.data;
    },
    removeImage() {
      this.lastProfileImage = this.currentProfileImage;
      this.currentProfileImage = null;
      this.profileImg = null;
      this.file = null;
      if (this.timer) {
        clearTimeout(this.timer);
      }
    },
    submitContact(isEdit) {
      const vm = this;
      const data = {
        last_name: vm.lastName,
        first_name: vm.firstName,
        email: vm.email,
        locale: vm.locale && vm.locale.value,
        street: vm.street,
        zip: vm.zip,
        city: vm.city,
        country: vm.country && vm.country.value,
        company: vm.company,
        tel: vm.tel,
        mobile: vm.mobile,
        fax: vm.fax,
        website: vm.website,
        job_title: vm.jobTitle,
        job_unit: vm.jobUnit,
        gender: vm.gender && vm.gender.value,
        timezone: vm.timezone ? vm.timezone.value : "Europe/Berlin",
        users: [],
        social_media_accounts: this.socialMediaAccounts,
      };
      vm.website = vm.website && vm.website.toLowerCase();
      if (vm.website && vm.website.substr(0, 7) !== "http://" && vm.website.substr(0, 8) !== "https://") {
        vm.website = "http://" + vm.website;
        data.website = vm.website;
      }
      for (var u in this.users) {
        data.users.push(this.users[u].uid);
      }
      vm.$validator.validateAll().then((result) => {
        if (result) {
          if (this.customFileErrors.length > 0) {
            this.currentProfileImage = null;
            this.profileImg = null;
            this.file = null;
            this.customFileErrors = [];
          }
          if (isEdit) {
            if (vm.lastProfileImage) {
              if (
                this.currentContact.image &&
                (this.currentContact.image.status & this.statiBits.COMPLETE.bit) === this.statiBits.COMPLETE.bit
              ) {
                this.deleteContactImage({
                  contact: this.currentContact.uid,
                  file: this.currentContact.image.uid,
                });
              }
            }
            vm.editContact({
              params: { contact: vm.$route.params.uid },
              data,
              redirect: true,
            })
              .then(() => {
                if (vm.file && vm.profileImg) {
                  const imageData = {
                    params: {
                      contact: vm.$route.params.uid,
                    },
                    image: {
                      file_size: vm.profileImg.size,
                      mime_type: vm.profileImg.type,
                      file_name: vm.profileImg.name,
                    },
                    file: this.file,
                  };
                  vm.createContactImage({
                    redirect: true,
                    uid: vm.$route.params.uid,
                    imageData,
                  })
                    .then(() => {
                      vm.$snotify.success(vm.$t("toasts.editedContact.success"), vm.$t("toasts.success"), {
                        icon: vm.$successIcon,
                      });
                      vm.$goTo("Contact", { uid: vm.$route.params.uid });
                    })
                    .catch((err) => {
                      if (err.response && !err.response.data.error.custom) {
                        vm.$snotify.error(vm.$t("toasts.createdContactImage.error"), vm.$t("toasts.error"), {
                          timeout: 0,
                          icon: vm.$errorIcon,
                        });
                      }
                      vm.$snotify.success(vm.$t("toasts.editedContact.success"), vm.$t("toasts.success"), {
                        icon: vm.$successIcon,
                      });
                      vm.$goTo("Settings", { tab: "contacts" });
                    });
                } else {
                  vm.$snotify.success(vm.$t("toasts.editedContact.success"), vm.$t("toasts.success"), {
                    icon: vm.$successIcon,
                  });
                  vm.$goTo("Contact", { uid: vm.$route.params.uid });
                }
              })
              .catch((err) => {
                if (err.response && !err.response.data.error.custom) {
                  vm.$snotify.error(vm.$t("toasts.editedContact.error"), vm.$t("toasts.error"), {
                    timeout: 0,
                    icon: vm.$errorIcon,
                  });
                }
              });
          } else {
            vm.createContact({ redirect: true, data })
              .then((res) => {
                if (vm.file && vm.profileImg) {
                  const imageData = {
                    params: {
                      contact: res.uid,
                    },
                    image: {
                      file_size: vm.profileImg.size,
                      mime_type: vm.profileImg.type,
                      file_name: vm.profileImg.name,
                    },
                    file: vm.file,
                  };
                  vm.createContactImage({
                    redirect: true,
                    uid: res.uid,
                    imageData,
                  })
                    .then(() => {
                      vm.$snotify.success(vm.$t("toasts.createdContact.success"), vm.$t("toasts.success"), {
                        icon: vm.$successIcon,
                      });
                      vm.$goTo("Contact", { uid: res.uid });
                    })
                    .catch((err) => {
                      if (err.response && !err.response.data.error.custom) {
                        vm.$snotify.error(vm.$t("toasts.createdContactImage.error"), vm.$t("toasts.error"), {
                          timeout: 0,
                          icon: vm.$errorIcon,
                        });
                      }
                      vm.$snotify.success(vm.$t("toasts.createdContact.success"), vm.$t("toasts.success"), {
                        icon: vm.$successIcon,
                      });
                      vm.$goTo("Settings", { tab: "contacts" });
                    });
                } else {
                  vm.$snotify.success(vm.$t("toasts.createdContact.success"), vm.$t("toasts.success"), {
                    icon: vm.$successIcon,
                  });
                  vm.$goTo("Contact", { uid: res.uid });
                }
              })
              .catch((err) => {
                if (err.response && !err.response.data.error.custom) {
                  vm.$snotify.error(vm.$t("toasts.createdContact.error"), vm.$t("toasts.error"), {
                    timeout: 0,
                    icon: vm.$errorIcon,
                  });
                }
              });
          }
        } else {
          vm.$snotify.error(vm.$t("toasts.contactForm.validation"), vm.$t("toasts.error"), {
            timeout: 0,
            icon: vm.$errorIcon,
          });
        }
      });
    },
    setValues() {
      this.lastName = this.currentContact.last_name;
      this.firstName = this.currentContact.first_name;
      this.email = this.currentContact.email;
      this.street = this.currentContact.street;
      this.zip = this.currentContact.zip;
      this.city = this.currentContact.city;
      this.country = this.currentContact.country;
      this.company = this.currentContact.company;
      this.tel = this.currentContact.tel;
      this.mobile = this.currentContact.mobile;
      this.fax = this.currentContact.fax;
      this.website = this.currentContact.website;
      this.jobTitle = this.currentContact.job_title;
      this.jobUnit = this.currentContact.job_unit;
      this.timezone = this.$t("timezones").find((obj) => {
        return obj.value === this.currentContact.timezone;
      });
      this.users = [];
      for (var u in this.currentContact.users) {
        this.users.push(this.currentContact.users[u]);
      }
      this.locale = this.localeOptions.find((obj) => {
        return obj.value === this.currentContact.locale;
      });
      this.gender = this.genderOptions.find((obj) => {
        return obj.value === this.currentContact.gender;
      });
      this.country = this.countryOptions.find((obj) => {
        return obj.value === this.currentContact.country;
      });
      for (const [key, value] of Object.entries(this.currentContact.social_media_accounts)) {
        this.socialMediaAccounts[key] = value;
      }
    },
    getItemPreview() {
      const vm = this;
      if (vm.isConverted) {
        vm.getContactImagePreview({
          customLoading: true,
          file: vm.currentContact.image.uid,
          contact: vm.currentContact.uid,
          width: "100",
          height: "100",
        })
          .then((res) => {
            vm.imageUrl = res;
          })
          .catch(() => {});
      } else {
        vm.imageUrl = "";
      }
    },
    untilReady() {
      this.getItemPreview();
      const data = {
        contact: this.currentContact.uid,
      };
      if (!this.isReady) {
        const vm = this;
        vm.timer = setTimeout(function () {
          vm.getContact(data)
            .then((response) => {
              vm.currentProfileImage = response.image;
              vm.amountTry = vm.amountTry - 1;
              if (vm.amountTry <= 0) {
                vm.amountTry = 10;
                vm.timeWaitStart = vm.timeWaitStart + 60000;
              }
              vm.untilReady();
            })
            .catch();
        }, vm.timeWaitStart);
      }
    },
    isConvertedCalculate(item) {
      return this.statiBits && item ? this.$h.globalIsBitSet(item.status, this.statiBits.CONVERTED.bit) : false;
    },
    isReadyCalculate(item) {
      if (this.statiBits && item) {
        return (
          this.$h.globalCheckFileStatus(item.status, this.statiBits) !== "VIRUS_SCANNING" &&
          this.$h.globalCheckFileStatus(item.status, this.statiBits) !== "CONVERTING"
        );
      } else {
        return false;
      }
    },
    isDownloadableCalculate(item) {
      return this.statiBits && item
        ? this.$h.globalIsBitSet(item.status, this.statiBits.VIRUS_SCAN_RESULT_OK.bit) ||
            this.$h.globalIsBitSet(item.status, this.statiBits.VIRUS_SCAN_RESULT_NA.bit)
        : false;
    },
  },
  computed: {
    isConverted() {
      return this.isConvertedCalculate(this.currentProfileImage);
    },
    isReady() {
      return this.isReadyCalculate(this.currentProfileImage);
    },
    isDisabled() {
      return !this.isDownloadable;
    },
    isDownloadable() {
      return this.isDownloadableCalculate(this.currentProfileImage);
    },
    currentStatus() {
      return this.statiBits && this.currentProfileImage
        ? this.$h.globalGetCurrentFileStatus(this.currentProfileImage.status, this.statiBits)
        : "";
    },
    ...mapGetters({
      teams: "auth_get_teams",
      currentContact: "teams_get_current_contact",
      teamLanguages: "teams_get_languages",
      teamUsers: "user_get_users",
      statiBits: "global_get_fileStatiBits",
    }),
  },
};
</script>
